import axios from "axios";
import { userManager } from "app/config/oidc";
import { BFF_ENABLED, bffLogin } from "app/config/bff";
import { DESIGN_PREVIEW } from "app/config/designPreview";

/**
 * 액세스 토큰이 만료되거나 폐기된 상태로 API 를 호출했을 때 복구한다.
 *
 * ── 왜 필요한가
 *
 * 지금까지 이 자리의 401 처리는 전부 주석 처리되어 있었다. 토큰 수명이 길어 좀처럼
 * 드러나지 않았지만, 만료·폐기가 실제로 일어나면 사용자는 <b>이유 없이 깨진 화면</b>을
 * 본다. 특히 통합 로그아웃으로 토큰이 폐기된 경우, 다른 탭은 아무 안내 없이 멈춘다.
 *
 * ── 어떻게
 *
 * 401 을 받으면 조용히 토큰을 갱신하고 그 요청만 한 번 재시도한다. 갱신까지 실패하면
 * 세션이 끝난 것이므로 로그인으로 보낸다.
 *
 * ★ 재시도는 한 번뿐이다. 표시를 남기지 않으면 갱신 직후에도 401 이 오는 상황
 *   (권한 회수·계정 비활성)에서 무한 루프가 된다.
 *
 * ★ 이 이음매는 인증 구조를 BFF 로 옮긴 뒤에도 남는다. 그때는 signinSilent 대신
 *   게이트웨이 로그인으로 리다이렉트하도록 이 안의 동작만 바뀐다.
 */

/** 이 요청이 이미 갱신 후 재시도된 것인지 표시한다. */
const RETRY_FLAG = "__ssoRetried";

/**
 * BFF 를 켜면 쿠키로 인증한다.
 *
 * ★ 세션 쿠키가 요청에 실리도록 한다. 같은 오리진이라 대개 자동으로 실리지만, 절대
 *   주소로 부르는 곳이 섞여 있어 명시한다.
 *
 * ★ CSRF 토큰을 함께 보낸다. 쿠키로 인증하면 다른 사이트가 사용자의 브라우저를 통해
 *   우리 API 를 부를 수 있으므로 게이트웨이가 CSRF 를 켰다. axios 는 XSRF-TOKEN 쿠키를
 *   읽어 X-XSRF-TOKEN 헤더로 되돌려주는데, withCredentials 가 켜져 있어야 그 동작이
 *   활성화된다. 이것이 빠지면 조회는 되는데 저장만 403 이 나고, 증상만 봐서는 권한
 *   문제로 보인다.
 */
if (BFF_ENABLED) {
  axios.defaults.withCredentials = true;
  axios.defaults.xsrfCookieName = "XSRF-TOKEN";
  axios.defaults.xsrfHeaderName = "X-XSRF-TOKEN";
}

axios.interceptors.request.use(req => {
    return req;
});

axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;
        const request = error?.config;

        if (status === 401 && request && !DESIGN_PREVIEW && !(request as any)[RETRY_FLAG]) {
            (request as any)[RETRY_FLAG] = true;

            // BFF 에서는 화면이 갱신할 토큰을 갖고 있지 않다. 세션이 끝났다는 뜻이므로
            // 로그인으로 보낸다 — 갱신은 게이트웨이가 이미 시도한 뒤다(세션의 리프레시
            // 토큰으로). 여기까지 401 이 왔다면 더 해 볼 것이 없다.
            if (BFF_ENABLED) {
                // 브라우저가 실제로 이동할 때만 멈춘다.
                //
                // ★ 무조건 멈추면 안 된다. 로그인이 방금 실패해 돌아온 경우 bffLogin 은
                //   이동시키지 않는데(무한 반복 방지), 그때도 멈춰 버리면 이 요청이
                //   영영 끝나지 않는다. 호출자가 기다리다 화면이 빈 채로 굳는다.
                if (bffLogin()) {
                    // 이동 중이다. 화면이 오류를 그리지 않도록 이 요청은 끝내지 않는다.
                    return new Promise(() => {});
                }

                // 이동하지 않았다. 아래 갱신 경로로 <흘려보내면 안 된다> — BFF 에는
                // 화면이 갱신할 토큰이 없고, signinSilent 는 있지도 않은 저장소를
                // 뒤지다 실패한다. 바로 거절해서 호출자가 '로그인 안 됨'으로 처리하게
                // 한다.
                return rejectWith(error);
            }

            try {
                const user = await userManager.signinSilent();
                if (user?.access_token) {
                    request.headers = request.headers ?? {};
                    request.headers.Authorization = `${user.token_type} ${user.access_token}`;
                    return await axios.request(request);
                }
            } catch {
                // 갱신이 실패했다. 세션이 끝난 것이므로 아래에서 로그인으로 보낸다.
            }
            try {
                await userManager.signinRedirect();
            } catch {
                // 리다이렉트조차 실패하면 아래 기존 처리로 넘어간다.
            }
        }

        return rejectWith(error);
    }
);

/**
 * 기존 거절 형식을 유지한다.
 *
 * 화면 여러 곳이 이 모양(응답 본문 또는 메시지)을 기대하므로, 새 경로에서도 같은 것을
 * 돌려줘야 한다.
 */
function rejectWith(error: any): Promise<never> {
    if (error?.response?.data) {
        return Promise.reject(error.response.data);
    }
    return Promise.reject(error?.message);
}
